import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCarrot, IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeleteIngredient, useIngredients } from './ingredientsApi';
import { IngredientFormDrawer } from './IngredientFormDrawer';
import type { IngredientDto } from './types';

export function IngredientsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data: ingredients, isLoading } = useIngredients();
  const deleteMutation = useDeleteIngredient();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<IngredientDto | null>(null);
  const [deleting, setDeleting] = useState<IngredientDto | null>(null);

  const filtered = useMemo(() => {
    if (!ingredients) return [];
    const q = search.trim().toLowerCase();
    if (!q) return ingredients;
    return ingredients.filter(
      (g) =>
        g.sku.toLowerCase().includes(q) ||
        g.nameAr.toLowerCase().includes(q) ||
        (g.nameEn ?? '').toLowerCase().includes(q),
    );
  }, [ingredients, search]);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (g: IngredientDto) => {
    setEditing(g);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.ingredientId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasRows = filtered.length > 0;
  const hasAny = (ingredients?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('ingredients.page.title')}
        subtitle={t('ingredients.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('ingredients.page.create')}
          </Button>
        }
      />

      <Paper p="md">
        <TextInput
          placeholder={t('ingredients.search.placeholder')}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
      </Paper>

      <Paper p={hasRows ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasAny ? (
          <EmptyState
            icon={<IconCarrot size={22} stroke={1.6} />}
            title={t('ingredients.empty.title')}
            description={t('ingredients.empty.body')}
            actionLabel={t('ingredients.page.create')}
            onAction={openCreate}
          />
        ) : !hasRows ? (
          <EmptyState
            icon={<IconSearch size={22} stroke={1.6} />}
            title={t('ingredients.empty.noMatch')}
            description={t('ingredients.empty.noMatchBody')}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('ingredients.columns.name')}</Table.Th>
                <Table.Th>{t('ingredients.columns.sku')}</Table.Th>
                <Table.Th>{t('ingredients.columns.unit')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('ingredients.columns.cost')}</Table.Th>
                <Table.Th>{t('ingredients.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((g) => (
                <Table.Tr key={g.ingredientId}>
                  <Table.Td>
                    <div>
                      <Text fw={500} c="var(--wheels-ink-strong)">
                        {g.nameAr}
                      </Text>
                      {g.nameEn && (
                        <Text fz="xs" c="var(--wheels-ink-muted)">
                          {g.nameEn}
                        </Text>
                      )}
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">
                      {g.sku}
                    </span>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{g.unitOfMeasureCode ?? '—'}</Table.Td>
                  <Table.Td style={{ textAlign: 'end' }}>
                    <Text className="font-numeric" c="var(--wheels-ink-strong)">
                      {g.costPrice != null ? g.costPrice.toFixed(2) : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {g.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(g)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(g)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <IngredientFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ingredient={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('ingredients.delete.title')}
        message={t('ingredients.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}
